import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock, Delete } from "lucide-react";

const UNLOCK_KEY = "stellar_pin_unlocked_until";

// ─── PinGuard: wraps children, shows the dialog until user unlocks ────
export default function PinGuard({ children }) {
  const [_, setTick] = useState(0);
  const navigate = useNavigate();
  const { data: cfg } = useQuery({
    queryKey: ["business-config"],
    queryFn: async () => (await api.get("/business/config")).data,
  });
  const enabled = !!cfg?.pin_enabled;
  const lockMinutes = Math.max(1, Number(cfg?.pin_lock_minutes || 5));

  // Compute unlock status; re-checks on tick.
  const unlockedUntil = readUnlockedUntil();
  const unlocked = !enabled || (unlockedUntil > Date.now());

  // Bump activity to slide the auto-lock timer forward.
  useEffect(() => {
    if (!enabled || !unlocked) return undefined;
    const bump = () => extendUnlock(lockMinutes);
    ["mousemove", "keydown", "click", "touchstart"].forEach((e) => window.addEventListener(e, bump, { passive: true }));
    // re-check periodically so the guard flips back to locked once idle
    const interval = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => {
      ["mousemove", "keydown", "click", "touchstart"].forEach((e) => window.removeEventListener(e, bump));
      clearInterval(interval);
    };
  }, [enabled, unlocked, lockMinutes]);

  if (!enabled || unlocked) return children;

  return (
    <PinPrompt
      onCancel={() => {
        const historyIndex = Number(window.history.state?.idx || 0);
        if (historyIndex > 0) navigate(-1);
        else navigate("/kalender", { replace: true });
      }}
      onSuccess={() => {
        extendUnlock(lockMinutes);
        setTick((n) => n + 1);
      }}
    />
  );
}

function readUnlockedUntil() {
  try {
    const v = sessionStorage.getItem(UNLOCK_KEY);
    return v ? Number(v) : 0;
  } catch { return 0; }
}

function extendUnlock(minutes) {
  try { sessionStorage.setItem(UNLOCK_KEY, String(Date.now() + minutes * 60_000)); } catch { /* ignore */ }
}

export function clearPinUnlock() {
  try { sessionStorage.removeItem(UNLOCK_KEY); } catch { /* ignore */ }
}

// ─── PIN prompt (full-screen numeric keypad) ─────────────────────────
function PinPrompt({ onSuccess, onCancel }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const submit = async (finalPin) => {
    if (finalPin.length !== 4) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/pin/verify", { pin: finalPin });
      if (data.valid) {
        onSuccess?.();
        return;
      }
      setErr("Falscher PIN");
      setPin("");
    } catch (e) {
      setErr(apiError(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const push = (d) => {
    if (busy || pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) submit(next);
  };
  const backspace = () => setPin((p) => p.slice(0, -1));

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center p-4" data-testid="pin-prompt">
      <div className="w-full max-w-sm">
        <button
          type="button"
          onClick={onCancel}
          className="mb-5 inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          data-testid="pin-cancel-btn"
        >
          <ArrowLeft className="h-5 w-5" /> Zurück
        </button>
        <div className="text-center mb-8">
          <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold">PIN erforderlich</h1>
          <p className="mt-1 text-sm text-muted-foreground">Diese Seite enthält finanzielle Informationen.</p>
        </div>

        {/* Hidden input for accessibility + keyboard entry */}
        <input
          ref={inputRef}
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          value={pin}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 4);
            setPin(v);
            if (v.length === 4) submit(v);
          }}
          className="sr-only"
          data-testid="pin-input"
          aria-label="PIN eingeben"
        />

        {/* Dots */}
        <div className="flex justify-center gap-4 mb-6" data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-4 w-4 rounded-full transition-colors ${pin.length > i ? "bg-primary" : "bg-muted"}`}
            />
          ))}
        </div>

        {err && <div className="text-center text-sm text-destructive mb-4 animate-pulse" data-testid="pin-error">{err}</div>}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <KeypadBtn key={n} onClick={() => push(String(n))} disabled={busy} testid={`pin-key-${n}`}>{n}</KeypadBtn>
          ))}
          <div />
          <KeypadBtn onClick={() => push("0")} disabled={busy} testid="pin-key-0">0</KeypadBtn>
          <KeypadBtn onClick={backspace} disabled={busy || pin.length === 0} testid="pin-key-back">
            <Delete className="h-6 w-6" />
          </KeypadBtn>
        </div>
      </div>
    </div>
  );
}

function KeypadBtn({ children, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="h-16 rounded-2xl border border-border/60 bg-card hover:bg-muted text-2xl font-display font-semibold transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
    >
      {children}
    </button>
  );
}

// ─── PIN Settings section (used inside Settings page) ─────────────────
export function PinSettingsSection() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({
    queryKey: ["business-config"],
    queryFn: async () => (await api.get("/business/config")).data,
  });
  const enabled = !!cfg?.pin_enabled;
  const lockMinutes = Number(cfg?.pin_lock_minutes || 5);

  const [mode, setMode] = useState(null);   // 'set' | 'change' | 'disable' | null
  const [current, setCurrent] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setMode(null); setCurrent(""); setNewPin(""); };

  const doSet = async () => {
    if (newPin.length !== 4) { toast.error("Neuer PIN muss 4 Ziffern haben."); return; }
    setBusy(true);
    try {
      await api.post("/pin/set", { new_pin: newPin, current_pin: current || null });
      toast.success(mode === "set" ? "PIN aktiviert" : "PIN geändert");
      qc.invalidateQueries({ queryKey: ["business-config"] });
      clearPinUnlock();
      reset();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doDisable = async () => {
    if (current.length !== 4) { toast.error("Aktuellen PIN eingeben."); return; }
    setBusy(true);
    try {
      await api.post("/pin/disable", { current_pin: current });
      toast.success("PIN deaktiviert");
      qc.invalidateQueries({ queryKey: ["business-config"] });
      clearPinUnlock();
      reset();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const setLockMinutes = async (n) => {
    try {
      await api.post("/business/config", { ...cfg, pin_lock_minutes: n });
      toast.success("Sperrzeit aktualisiert");
      qc.invalidateQueries({ queryKey: ["business-config"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const LOCK_OPTIONS = [1, 5, 15, 30];

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5" data-testid="pin-settings-section">
      <h2 className="font-display font-semibold text-lg mb-1 flex items-center gap-2">
        <Lock className="h-4 w-4" /> PIN-Schutz
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Schützen Sie Übersicht, Rechnungen, Zahlungen, Notizen und Archiv vor neugierigen Blicken.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
          enabled ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground"
        }`} data-testid="pin-status">
          <Lock className="h-3 w-3" /> {enabled ? "Aktiviert" : "Deaktiviert"}
        </span>
        {!enabled && !mode && (
          <button
            className="text-sm text-primary hover:underline"
            onClick={() => setMode("set")}
            data-testid="pin-enable-btn"
          >
            PIN aktivieren
          </button>
        )}
        {enabled && !mode && (
          <>
            <button className="text-sm text-primary hover:underline" onClick={() => setMode("change")} data-testid="pin-change-btn">PIN ändern</button>
            <button className="text-sm text-destructive hover:underline" onClick={() => setMode("disable")} data-testid="pin-disable-btn">Deaktivieren</button>
          </>
        )}
      </div>

      {(mode === "set" || mode === "change") && (
        <div className="space-y-2 mb-4 p-3 rounded-md border border-border/60 bg-muted/30">
          {mode === "change" && (
            <PinField label="Aktueller PIN" value={current} onChange={setCurrent} testid="pin-current-input" />
          )}
          <PinField label="Neuer PIN (4 Ziffern)" value={newPin} onChange={setNewPin} testid="pin-new-input" />
          <div className="flex gap-2 pt-2">
            <button onClick={reset} className="px-3 py-1.5 rounded-md border text-sm">Abbrechen</button>
            <button onClick={doSet} disabled={busy} data-testid="pin-submit" className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50">
              {mode === "set" ? "Aktivieren" : "PIN ändern"}
            </button>
          </div>
        </div>
      )}

      {mode === "disable" && (
        <div className="space-y-2 mb-4 p-3 rounded-md border border-destructive/40 bg-destructive/5">
          <PinField label="Aktueller PIN" value={current} onChange={setCurrent} testid="pin-current-input" />
          <div className="flex gap-2 pt-2">
            <button onClick={reset} className="px-3 py-1.5 rounded-md border text-sm">Abbrechen</button>
            <button onClick={doDisable} disabled={busy} data-testid="pin-submit" className="px-3 py-1.5 rounded-md bg-destructive text-destructive-foreground text-sm disabled:opacity-50">
              Deaktivieren
            </button>
          </div>
        </div>
      )}

      {enabled && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">Automatisch sperren nach Inaktivität</div>
          <div className="inline-flex rounded-md border border-border/60 p-0.5 bg-muted/40" data-testid="pin-lock-minutes">
            {LOCK_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => setLockMinutes(n)}
                data-testid={`pin-lock-${n}`}
                className={`px-3 py-1 text-sm rounded ${lockMinutes === n ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
              >
                {n} Min
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PinField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <input
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
        data-testid={testid}
        className="w-32 px-3 py-2 rounded-md border border-border/60 bg-background text-center text-xl font-mono tabular-nums tracking-[0.5em]"
      />
    </div>
  );
}
